import { useCallback, useEffect, useState } from "react";
import { NotAuthenticatedError } from "./api";

interface Loadable<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Loads data from the Control API, with one deliberate behaviour: a 401 is
 * re-thrown to `onSignedOut` rather than rendered as an error, because a
 * dead session is not something the page can show a useful message about —
 * the whole app has to return to the login screen.
 */
export function useApiData<T>(
  load: () => Promise<T>,
  onSignedOut: () => void,
  deps: unknown[] = [],
): Loadable<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) {
          onSignedOut();
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  return { data, error, loading, reload };
}
